import { useNavigationContext } from '../../';
import { useCallback, useEffect, useMemo } from 'react';
import { useWizard as useBaseWizard } from 'react-use-wizard';

export default function useWizardLogic({ urlControl, stepParamName }: { urlControl?: boolean; stepParamName: string }) {
  const wizardValues = useBaseWizard();
  const { activeStep, goToStep: baseGoToStep, nextStep: baseNextStep, previousStep: basePreviousStep } = wizardValues;

  const { router } = useNavigationContext();
  const cleanQuery = useMemo(
    () =>
      router?.query &&
      Object.keys(router?.query)
        .filter((k) => k !== 'id')
        .reduce((acc, k) => ({ ...acc, [k]: router?.query[k] }), {}),
    [router?.query]
  );

  const nextStep = useCallback(() => {
    if (!urlControl) return wizardValues.nextStep();
    router?.push({
      pathname: window.location.pathname,
      query: cleanQuery
        ? { ...cleanQuery, [stepParamName]: (activeStep + 1).toString() }
        : { [stepParamName]: (activeStep + 1).toString() },
    });
    // empty promise
    return new Promise<void>((resolve) => resolve());
  }, [wizardValues, activeStep, cleanQuery, urlControl, stepParamName, router]);

  const previousStep = useCallback(() => {
    if (!urlControl) return wizardValues.previousStep();
    router.push({
      pathname: window.location.pathname,
      query: cleanQuery
        ? { ...cleanQuery, [stepParamName]: (activeStep - 1).toString() }
        : { [stepParamName]: (activeStep - 1).toString() },
    });
  }, [wizardValues, activeStep, cleanQuery, urlControl, stepParamName, router]);

  const goToStep = useCallback(
    (index: number) => {
      if (!urlControl) return wizardValues.goToStep(index);
      router.push({
        pathname: window.location.pathname,
        query: cleanQuery
          ? { ...cleanQuery, [stepParamName]: index.toString() }
          : { [stepParamName]: index.toString() },
      });
    },
    [wizardValues, cleanQuery, urlControl, stepParamName, router]
  );

  const value = useMemo(() => {
    if (!urlControl) return wizardValues;

    return {
      ...wizardValues,
      nextStep,
      previousStep,
      goToStep,
    };
  }, [wizardValues, nextStep, previousStep, goToStep, urlControl]);

  // Side effects to connect internal state with url params
  useEffect(() => {
    // Init side effect
    if (!urlControl) return;
    if (!router?.isReady) return;
    if (router?.query[stepParamName] === activeStep.toString()) return;

    if (!router?.query[stepParamName] || activeStep === 0) {
      // Step 0
      router?.push({
        pathname: window.location.pathname,
        query: { ...cleanQuery, [stepParamName]: '0' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, router?.isReady, urlControl]);

  // This side effect is actually modifying the internal state of the Wizard
  useEffect(() => {
    if (!urlControl) return;
    if (!router?.isReady) return;
    if (!router?.query[stepParamName]) return;

    const step = Number(router?.query[stepParamName]);
    if (step === activeStep) return;

    if (step === activeStep + 1) baseNextStep();
    else if (step === activeStep - 1) basePreviousStep();
    else baseGoToStep(step);
  }, [
    router?.isReady,
    router?.query,
    urlControl,
    stepParamName,
    baseGoToStep,
    baseNextStep,
    basePreviousStep,
    activeStep,
  ]);

  return { value };
}
